import { useState } from "react";
import { TenantsClient, type CreatedTenant, type TenantSummary } from "../api/generated";
import { usePermissions } from "../auth/SessionContext";
import { Alert, Empty, Field, Panel, describeError, useAsync, when } from "../components/ui";

const tenants = new TenantsClient("");

/**
 * Console module: <b>Tenants</b> — platform administrators only.
 *
 * A company administrator never sees this: the session context already reports `tenants` as NONE
 * for them, and the sidebar renders what the API reports (§8.3, §10). Hiding it is convenience;
 * the API is the security.
 */
export function TenantsPage() {
  const { hasCapability } = usePermissions();
  const { data, error, loading, reload } = useAsync(() => tenants.tenants_List());
  const [actionError, setActionError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedTenant | null>(null);

  const canAdmin = hasCapability("tenants:admin");

  async function setActive(tenant: TenantSummary, isActive: boolean) {
    setActionError(null);
    try {
      await tenants.tenants_SetActive(tenant.id!, { isActive });
      await reload();
    } catch (e) {
      setActionError(describeError(e));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Client companies</h1>
          <p className="sub">
            Each client company is a separate tenant. Creating one sets up its default location,
            starting flags and first administrator, who completes onboarding from their own console.
          </p>
        </div>
      </div>

      {error && <Alert kind="error">{error}</Alert>}
      {actionError && <Alert kind="error">{actionError}</Alert>}

      {created && (
        <Panel title="Company created">
          <Alert kind="ok">
            <strong>{created.tenant?.name}</strong> is ready. Its administrator signs in as{" "}
            <span className="mono">{created.adminEmail}</span> with the password you just set, and
            defines the company's attributes, parties, streams and flags from there.
          </Alert>
          <button className="ghost small" onClick={() => setCreated(null)}>Dismiss</button>
        </Panel>
      )}

      {canAdmin && <CreateTenant onCreated={(t) => { setCreated(t); void reload(); }} />}

      <Panel title="Companies">
        {loading ? (
          <Empty>Loading…</Empty>
        ) : (data ?? []).length === 0 ? (
          <Empty>No client companies yet.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Key</th><th>Name</th><th>Parties</th><th>Created</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data!.map((t) => (
                <tr key={t.id}>
                  <td className="mono">{t.key}</td>
                  <td>{t.name}</td>
                  <td>{t.partyCount}</td>
                  <td className="muted">{when(t.createdAt)}</td>
                  <td>
                    {t.isActive
                      ? <span className="chip ok">active</span>
                      : <span className="chip warn">suspended</span>}
                  </td>
                  <td>
                    {canAdmin && (
                      <button
                        className={t.isActive ? "danger small" : "ghost small"}
                        onClick={() => setActive(t, !t.isActive)}
                      >
                        {t.isActive ? "Suspend" : "Restore"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
          Suspending a company blocks every credential it holds at the next request. Nothing is
          deleted, and it can be reversed.
        </p>
      </Panel>
    </>
  );
}

function CreateTenant({ onCreated }: { onCreated: (created: CreatedTenant) => void }) {
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ready = key && name && adminName && adminEmail && adminPassword.length >= 12;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const created = await tenants.tenants_Create({ key, name, adminName, adminEmail, adminPassword });
      setKey(""); setName(""); setAdminName(""); setAdminEmail(""); setAdminPassword("");
      onCreated(created);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Add a client company">
      {error && <Alert kind="error">{error}</Alert>}
      <div className="row">
        <Field label="Key">
          <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="acme-foods" />
        </Field>
        <Field label="Company name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Foods" />
        </Field>
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <Field label="Administrator name">
          <input value={adminName} onChange={(e) => setAdminName(e.target.value)} />
        </Field>
        <Field label="Administrator email">
          <input value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />
        </Field>
        <Field label="Temporary password">
          <input
            type="password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
          />
        </Field>
        <button onClick={submit} disabled={busy || !ready}>Create</button>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
        The key is lowercase and permanent. The administrator gets every module except Tenants and
        Platform. Passwords must be at least 12 characters.
      </p>
    </Panel>
  );
}
